/**
 * useDoorShortcuts — the quality door's keyboard layer (proposal 9).
 *
 * A keyboard-only tester (RSI) found no shortcuts and ~20 Tabs to the first
 * item. Mounted once by the shell:
 *   j / k      focus the next / previous item on the current list
 *   Enter      open the focused item
 *   o s d n a  Open investigation · Send to someone else · Done · Not an
 *              issue · Ask a colleague — they press the page's own button
 *              (`data-shortcut="<key>"`), so only when it is there and
 *              enabled, and through the very same handler
 *   /          focus the search
 *   g t · g c · g d   Triage · Cases · Done
 *   ?          show / hide the legend; Esc closes it
 * Nothing fires while the person types (input, textarea, select,
 * contenteditable), with Ctrl / Alt / Cmd held, or while a modal dialog
 * (the answer drawer, the Ask form) owns the keyboard.
 *
 * Nor in a run of typing that missed its box (UX round 2026-10-20): a click
 * meant for the note box landed beside it, and the sentence typed next
 * pressed Send to someone else ("s"), closing the half-filled panel, then
 * Ask ("a"), opening an ask nobody wanted. A key that follows a
 * non-shortcut character key ("T", "e", a space) within TYPING_BURST_MS is
 * typing; shortcuts pressed one after another (j j j, g t) still work.
 */
import { useEffect, useRef } from 'react';

import { trackEvent } from '../../services/portalTracking';
import { doorItems, shortcutTarget } from './doorFocus';

export type DoorTab = 'triage' | 'cases' | 'done' | 'pending';

/** The keys that press one of the item page's decision buttons. */
export const ACTION_KEYS = ['o', 's', 'd', 'n', 'a'] as const;

const TAB_KEYS: Record<string, DoorTab> = { t: 'triage', c: 'cases', d: 'done', p: 'pending' };

/** How long "g" waits for its second key. */
export const G_SEQUENCE_MS = 1500;

/** A character key this soon after another one is typing, not a shortcut.
 *  "g t" stays a shortcut: its second key is read before this check. */
export const TYPING_BURST_MS = 700;

/** True when a key at `now` follows a stray character key (one that was no
 *  shortcut) closely enough to be the same run of typing. */
export function continuesTyping(previousStrayAt: number | null, now: number): boolean {
  return previousStrayAt !== null && now - previousStrayAt < TYPING_BURST_MS;
}

export interface DoorShortcutOptions {
  goTab: (tab: DoorTab) => void;
  legendOpen: boolean;
  setLegendOpen: (open: boolean) => void;
}

/** True when the key belongs to whatever the person is typing into. */
export function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable || target.closest('[contenteditable="true"], [contenteditable=""]')) return true;
  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
}

function modalOpen(): boolean {
  return !!document.querySelector('[aria-modal="true"]');
}

function used(key: string, outcome: string, extra?: Record<string, unknown>) {
  trackEvent('action', 'shortcut_used', { key, outcome, ...extra });
}

/** Move the focus ring one item along the list (j = +1, k = -1). */
function stepItems(delta: 1 | -1): HTMLElement | null {
  const items = doorItems();
  if (items.length === 0) return null;
  const active = document.activeElement;
  const at = items.findIndex((el) => el === active || el.contains(active));
  const next = at === -1 ? (delta === 1 ? 0 : items.length - 1) : Math.min(Math.max(at + delta, 0), items.length - 1);
  const el = items[next];
  el.focus();
  el.scrollIntoView?.({ block: 'nearest' });
  return el;
}

export function useDoorShortcuts({ goTab, legendOpen, setLegendOpen }: DoorShortcutOptions): void {
  // The latest options, read by one listener added once.
  const opts = useRef({ goTab, legendOpen, setLegendOpen });
  useEffect(() => {
    opts.current = { goTab, legendOpen, setLegendOpen };
  });
  const gPending = useRef<number | null>(null);
  const lastStrayAt = useRef<number | null>(null);

  useEffect(() => {
    const clearG = () => {
      if (gPending.current !== null) window.clearTimeout(gPending.current);
      gPending.current = null;
    };

    // What one key does; true when it acted as a shortcut.
    const act = (e: KeyboardEvent, key: string): boolean => {
      const { goTab: go, legendOpen: open, setLegendOpen: setOpen } = opts.current;

      // Second key of "g t" / "g c" / "g d".
      if (gPending.current !== null) {
        clearG();
        const tab = TAB_KEYS[key];
        if (tab) {
          e.preventDefault();
          used(`g ${key}`, 'navigate', { tab });
          go(tab);
          return true;
        }
      }

      if (key === 'g') {
        gPending.current = window.setTimeout(clearG, G_SEQUENCE_MS);
        return true;
      }
      if (key === '?') {
        e.preventDefault();
        trackEvent('action', 'shortcut_legend_toggle', { open: !open, via: 'key' });
        setOpen(!open);
        return true;
      }
      if (key === 'Escape') {
        if (!open) return false;
        trackEvent('action', 'shortcut_legend_toggle', { open: false, via: 'escape' });
        setOpen(false);
        return true;
      }
      if (key === 'j' || key === 'k') {
        const el = stepItems(key === 'j' ? 1 : -1);
        if (!el) return false;
        e.preventDefault();
        used(key, 'focus_item', { item: el.dataset.doorItem ?? null });
        return true;
      }
      if (key === 'Enter') {
        // A row that is a native button or link opens itself on Enter.
        const active = document.activeElement as HTMLElement | null;
        if (!active?.matches('[data-door-item]') || active.tagName === 'BUTTON' || active.tagName === 'A') return false;
        e.preventDefault();
        used(key, 'open_item', { item: active.dataset.doorItem ?? null });
        active.click();
        return true;
      }
      if (key === '/') {
        const target = shortcutTarget('/');
        if (!target) return false;
        e.preventDefault();
        used(key, 'focus_search');
        target.focus();
        return true;
      }
      if ((ACTION_KEYS as readonly string[]).includes(key)) {
        const target = shortcutTarget(key);
        if (!target) return false;
        e.preventDefault();
        used(key, 'press', { control: target.dataset.shortcutName ?? null });
        target.click();
        return true;
      }
      return false;
    };

    const onKey = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.ctrlKey || e.metaKey || e.altKey) return;
      if (isTypingTarget(e.target) || isTypingTarget(document.activeElement)) return;
      if (modalOpen()) return;
      const key = e.key;
      // A character key that is no shortcut ("T", "e", a space) starts or
      // continues a run of typing; any key in that run is typing too, and
      // nothing is recorded about it (typed words are never recorded).
      // Shortcuts pressed one after another (j j j, g t) are no such run.
      const isChar = key.length === 1;
      const now = Date.now();
      if (isChar && gPending.current === null && continuesTyping(lastStrayAt.current, now)) {
        lastStrayAt.current = now;
        // Its spaces would page the screen down, away from the panel the
        // person was filling in.
        if (key === ' ') e.preventDefault();
        return;
      }
      const acted = act(e, key);
      if (isChar && !acted) lastStrayAt.current = now;
    };

    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      clearG();
    };
  }, []);
}
